import { useEffect, useState, type RefObject } from 'react';

/** Resolves a CSS length custom property (`3rem`, `48px`) on `:root` to pixels. */
export const cssVarPx = (name: string): number => {
  const root = document.documentElement;
  const value = getComputedStyle(root).getPropertyValue(name).trim();
  const amount = parseFloat(value);
  if (Number.isNaN(amount)) return 0;
  if (value.endsWith('rem')) return amount * parseFloat(getComputedStyle(root).fontSize);
  return amount;
};

/**
 * True once `sentinelRef` (a 0-height marker placed right above a `position: sticky` element)
 * has scrolled above the sticky app header, i.e. the element below it is pinned.
 */
export const useStuck = (sentinelRef: RefObject<HTMLElement | null>, enabled = true): boolean => {
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!enabled || !sentinel) {
      setStuck(false);
      return;
    }
    const offset = cssVarPx('--pw-header-height');
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const top = entry.rootBounds?.top ?? offset;
        setStuck(!entry.isIntersecting && entry.boundingClientRect.top < top);
      },
      { rootMargin: `-${offset}px 0px 0px 0px`, threshold: [0, 1] },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelRef, enabled]);

  return stuck;
};
